import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-button text-sm font-semibold transition-all duration-200 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Phase 3.2: Enhanced with scale and glow feedback
        default: [
          "bg-gradient-primary text-white shadow-button",
          "hover:shadow-button-hover hover:-translate-y-0.5 hover:brightness-105",
          "active:translate-y-0 active:brightness-100",
        ],
        destructive: [
          "bg-destructive text-destructive-foreground shadow-sm",
          "hover:bg-destructive/90 hover:shadow-glow-danger hover:scale-[1.02]",
          "active:scale-[0.98]",
        ],
        outline: [
          "border border-border bg-background",
          "hover:bg-background-elevated hover:scale-[1.02] hover:border-primary/50",
          "active:scale-100",
        ],
        secondary: [
          "bg-muted text-foreground border border-border",
          "hover:bg-muted/80 hover:scale-[1.02]",
          "active:scale-100",
        ],
        ghost: [
          "hover:bg-muted/50 hover:text-foreground",
          "active:bg-muted/80",
        ],
        link: [
          "text-primary underline-offset-4",
          "hover:underline hover:text-primary-dark",
        ],
        success: [
          "bg-success text-success-foreground shadow-sm",
          "hover:bg-success/90 hover:shadow-glow-success hover:scale-[1.02]",
          "active:scale-[0.98]",
        ],
        warning: [
          "bg-warning text-warning-foreground shadow-sm",
          "hover:bg-warning/90 hover:shadow-glow-warning hover:scale-[1.02]",
          "active:scale-[0.98]",
        ],
        gradient: [
          "bg-gradient-hero text-white shadow-button",
          "hover:shadow-button-hover hover:-translate-y-0.5 hover:brightness-105",
          "active:translate-y-0 active:brightness-100",
        ],
      },
      size: {
        default: "h-[44px] px-4 py-3 md:px-6",
        sm: "h-10 px-3 text-xs md:h-9",
        lg: "h-12 px-6 text-base md:px-8",
        icon: "h-11 w-11 md:h-10 md:w-10",
        "icon-sm": "h-9 w-9 md:h-8 md:w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
